import type { SeoLandingConfig } from "../types";

// Fatos com fonte em .claude/skills/seo/reference/fidexa-empresa.md
// (siglas: ERP = Portal ERP, NV = Novo Varejo).
// Ângulo: cobrança de crediário próprio do varejo, não billing de assinatura
// (keywords.md §1, linha "/" e §2.1, linha 4).
const SOURCE_ERP_NV = "Portal ERP e Novo Varejo, ago/2026";

export const sistemaDeCobranca: SeoLandingConfig = {
  path: "/sistema-de-cobranca",
  kind: "ferramenta",
  keyword: "sistema de cobrança para lojas",
  title: "Sistema de cobrança para lojas com crediário",
  description:
    "Sistema de cobrança para lojas com crediário próprio: régua automática no WhatsApp, Pix com baixa automática e a equipe negociando atrasos num chat da rede.",
  breadcrumbLabel: "Sistema de cobrança",
  updatedAt: "2026-10-17",
  service: {
    name: "Sistema de cobrança de crediário para lojas",
    serviceType:
      "Software de cobrança de inadimplentes do crediário próprio via WhatsApp",
    audience: "Lojas e redes do varejo que vendem no crediário próprio",
  },
  sections: [
    {
      type: "hero",
      eyebrow: "Software de cobrança para o varejo",
      title: "O sistema de cobrança para lojas que vendem no crediário próprio",
      lead: "A Fidexa organiza a cobrança inteira: a régua avisa antes do vencimento, o cliente paga por Pix no WhatsApp e a sua equipe trata os atrasos de todas as lojas numa tela só.",
      ctaLabel: "Agendar demonstração gratuita",
      secondaryLabel: "Ver o que o sistema faz",
      secondaryHref: "#como-funciona",
      reassurance: "Demonstração gratuita, com a carteira da sua rede.",
    },
    {
      type: "proof",
      title: "Redes do varejo que cobram o crediário com a Fidexa:",
      clientNames: [
        "Grupo Minas",
        "Lojas Elder",
        "Tangará",
        "Lojas Skala",
        "SuperModas",
        "Omara Calçados",
      ],
    },
    {
      type: "shortAnswer",
      id: "o-que-e-sistema",
      title: "O que é um sistema de cobrança para lojas",
      answer:
        "Sistema de cobrança para lojas é o software que acompanha cada parcela do crediário próprio, do vencimento à quitação. Na Fidexa, uma régua automática fala com o cliente no WhatsApp, o Pix do cliente cai na conversa já com baixa automática, e a equipe da loja negocia os atrasos num chat único da rede.",
      bullets: [
        "Feito para crediário do varejo, não para assinatura ou mensalidade.",
        "Conecta ao TOTVS Linx por API ou recebe a carteira por planilha.",
        "Dívida de qualquer valor entra na régua, até a de R$ 50, e também as já negativadas.",
      ],
    },
    {
      type: "steps",
      id: "como-funciona",
      title: "O que o sistema faz pela sua equipe de crediário",
      lead: "Quatro frentes que hoje dependem de alguém com tempo livre na loja.",
      steps: [
        {
          title: "Organiza a carteira",
          text: "Quem deve, quanto deve e quando vence: os dados vêm da API do TOTVS Linx ou de uma planilha exportada do seu sistema de gestão, que continua o mesmo.",
        },
        {
          title: "Dispara a régua sozinho",
          text: "Do aviso preventivo à cobrança do atraso, as mensagens partem da API oficial do WhatsApp no ritmo definido, sem ninguém lembrar de mandar.",
        },
        {
          title: "Abre espaço para negociar",
          text: "Quem atrasou e quer um acordo fala com a sua equipe no chat unificado da Fidexa: as conversas de todas as lojas num lugar só, separadas por unidade.",
        },
        {
          title: "Mostra a rede inteira",
          text: "O painel junta os débitos de todas as unidades, e a análise de conversão mostra quais cobranças estão dando resultado.",
        },
      ],
    },
    {
      type: "comparison",
      id: "comparativo-sistemas",
      title: "Cobrança manual, billing de assinatura ou Fidexa",
      lead: "Nem todo software de cobrança foi pensado para o crediário de loja.",
      caption:
        "Comparação entre cobrar inadimplentes do crediário próprio com equipe e planilha, com um sistema de cobrança recorrente de assinaturas e com a Fidexa",
      columns: [
        { key: "manual", label: "Equipe com planilha e telefone" },
        { key: "billing", label: "Sistema de cobrança recorrente" },
        { key: "fidexa", label: "Fidexa", highlight: true },
      ],
      rows: [
        {
          label: "Pensado para",
          cells: {
            manual: "O que der tempo",
            billing: "Assinaturas e mensalidades",
            fidexa: "Crediário próprio do varejo",
          },
        },
        {
          label: "Canal principal da cobrança",
          cells: {
            manual: "Ligação e WhatsApp do celular",
            billing: "Varia por ferramenta",
            fidexa: "API oficial do WhatsApp",
          },
        },
        {
          label: "Cliente paga sozinho, a qualquer hora",
          cells: {
            manual: false,
            billing: "Varia por ferramenta",
            fidexa: "Pix no Fidexa Pay, 24 horas",
          },
        },
        {
          label: "Onde a equipe negocia o atraso",
          cells: {
            manual: "Ligação e celular de cada loja",
            billing: "Varia por ferramenta",
            fidexa: "Chat único da rede",
          },
        },
        {
          label: "Carteira de várias lojas num lugar só",
          cells: { manual: false, billing: false, fidexa: "Painel único" },
        },
        {
          label: "Integração com TOTVS Linx",
          cells: { manual: false, billing: false, fidexa: "API ou planilha" },
        },
        {
          label: "Cobra dívida já negativada",
          cells: { manual: "Raramente", billing: false, fidexa: true },
        },
      ],
    },
    {
      type: "case",
      id: "caso-lojas-elder",
      title: "Lojas Elder: a inadimplência caiu quase pela metade",
      text: "Com mensagens automáticas e personalizadas no WhatsApp, o time financeiro da Lojas Elder recuperou clientes e reduziu atrasos sem aumentar custos operacionais.",
      metrics: [
        {
          value: "14% → 8%",
          label: "Inadimplência da Lojas Elder antes e depois da Fidexa",
          source: "Caso publicado pela Fidexa",
        },
      ],
    },
    {
      type: "metrics",
      id: "numeros-sistema",
      title: "O sistema em operação",
      metrics: [
        {
          value: "R$ 39,34 mi",
          label: "Devolvidos ao caixa de lojistas em menos de um ano",
          source: SOURCE_ERP_NV,
        },
        {
          value: "1.496.136",
          label: "Mensagens de cobrança enviadas",
          source: "Plataforma Fidexa",
        },
        {
          value: "249",
          label: "Empresas cadastradas na plataforma",
          source: SOURCE_ERP_NV,
        },
      ],
    },
    {
      type: "calculator",
      id: "calculadora-sistema",
      title: "Antes de escolher um sistema, meça o tamanho do problema",
      text: "Coloque os números do seu crediário e veja quanto a inadimplência tira do caixa da loja todo ano.",
      href: "/ferramentas/calculadora-de-inadimplencia",
      ctaLabel: "Usar a calculadora grátis",
    },
    {
      type: "faq",
      id: "perguntas-sistema",
      title: "Dúvidas comuns sobre sistema de cobrança para lojas",
      items: [
        {
          question: "O que faz um sistema de cobrança para lojas?",
          answer:
            "Automatiza o ciclo de cobrança do crediário: avisa que a parcela vai vencer, cobra o atraso, dá à equipe um canal para negociar e registra o pagamento. Na Fidexa tudo isso acontece no WhatsApp, com Pix na conversa e baixa automática da parcela.",
        },
        {
          question:
            "Qual a diferença entre a Fidexa e um sistema de cobrança recorrente?",
          answer:
            "Sistemas de cobrança recorrente são feitos para assinaturas e mensalidades. A Fidexa é um software de cobrança do crediário próprio do varejo: trabalha com carnê e parcelas de compra, cobra inadimplentes e deixa a equipe fechar acordos de parcelamento pelo chat.",
        },
        {
          question: "Como funciona a cobrança de inadimplentes na Fidexa?",
          answer:
            "Por uma régua automática no WhatsApp que escreve ao cliente antes de vencer, na data do vencimento e quando a parcela atrasa. O cliente pode pagar sozinho por Pix na conversa, a qualquer hora; quem precisa de acordo negocia com a equipe da loja no chat da Fidexa.",
        },
        {
          question: "Preciso trocar o sistema de gestão da loja?",
          answer:
            "Não. A Fidexa funciona ao lado do sistema que você já usa. Com o TOTVS Linx a conexão é por API; com outros ERPs, basta enviar a planilha da carteira.",
        },
        {
          question:
            "O sistema de cobrança serve para quem tem mais de uma loja?",
          answer:
            "Sim. O painel centraliza a carteira de todas as unidades e mostra os débitos pendentes de cada uma. O chat unificado controla vários números de WhatsApp num lugar só, separando o atendimento por loja.",
        },
        {
          question: "Como sei se a cobrança está dando resultado?",
          answer:
            "Pela análise de conversão, que monitora o desempenho das cobranças. Nas carteiras conectadas à Fidexa, a taxa média de recuperação é de 32,10%, segundo a plataforma.",
        },
        {
          question: "A Fidexa cobra dívidas antigas ou negativadas?",
          answer:
            "Sim. Débitos que já foram para os órgãos de proteção ao crédito também entram na régua, assim como débitos pequenos, de qualquer valor: uma parcela de R$ 50 sairia mais cara na Justiça do que o próprio valor devido.",
        },
        {
          question: "Cobrar pelo WhatsApp é seguro para a loja?",
          answer:
            "Na Fidexa, sim: a régua roda na API oficial do WhatsApp, segue o modelo de template da Meta, e a plataforma segue a LGPD no tratamento dos dados dos clientes.",
        },
        {
          question: "Quanto a Fidexa já recuperou para lojistas?",
          answer:
            "R$ 39,34 milhões em menos de um ano de operação, para 249 empresas cadastradas, segundo o Portal ERP e o Novo Varejo. A imprensa citou na época uma recuperação média de 20%; o número atual da plataforma é 32,10%.",
        },
        {
          question: "Quanto custa o sistema de cobrança da Fidexa?",
          answer:
            "O valor depende do tamanho da operação e da quantidade de lojas. Fale com um especialista: a demonstração é gratuita, roda com a sua carteira e termina com a proposta.",
        },
      ],
    },
    {
      type: "finalCta",
      id: "cta-final-sistema",
      title: "Coloque a cobrança da sua rede para rodar sem planilha",
      text: "Um especialista mostra o sistema com a carteira das suas lojas, em uma demonstração gratuita.",
      ctaLabel: "Agendar demonstração gratuita",
    },
  ],
};
